import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TooltipProvider } from "@/components/ui/tooltip";
import { formatNumberWithCommas, sanitizeNumberInput } from "@/lib/numberFormat";
import { INPUT_BOX_CLASS, INPUT_BOX_STYLE } from "./checkerFieldStyles";
import { ChoiceGroup, YesNoChoice } from "./ChoiceCard";
import FieldError from "./FieldError";
import { fieldAnchorProps, invalidInputProps } from "./checkerValidation";

const businessStageOptions = [
  "Startup",
  "Growth", 
  "Mature",
  "Established"
];

const cashRunwayOptions = [
  "Less than 3 months",
  "3–6 months",
  "6–12 months",
  "More than 12 months",
  "Prefer not to say",
  "Not sure"
];

export default function BusinessMetricsStep({ formData, updateFormData, errors = {} }) {
  return (
    <TooltipProvider>
      <div id="step-metrics" className="space-y-10">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold mb-3" style={{ color: '#4B5563' }} tabIndex={-1} data-step-heading>Business Metrics</h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#4B5563' }}>
            Financial and operational metrics to assess your business readiness
          </p>
        </div>

        {/* Form Fields */}
        <div className="space-y-8">
          {/* Business Metrics Section */}
          <div className="w-full rounded-3xl bg-white/5 backdrop-blur-xl shadow-2xl border border-white/10 p-6 md:p-8">
            <h3 className="mb-6 text-xl font-semibold" style={{ color: '#4B5563' }}>Business Metrics</h3>
            
            <div className="space-y-8">
              {/* Simple Fields Row */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3" {...fieldAnchorProps('employees')}>
                  <Label htmlFor="employees" className="font-medium" style={{ color: '#4B5563' }}>
                    Number of Employees <span style={{ color: '#4B5563' }}>*</span>
                  </Label>
                  <Input
                    id="employees"
                    name="employees"
                    type="text"
                    inputMode="numeric"
                    value={formatNumberWithCommas(formData.employees)}
                    onChange={(e) => {
                      const sanitized = sanitizeNumberInput(e.target.value);
                      updateFormData({ employees: sanitized });
                    }}
                    placeholder="e.g., 25"
                    style={INPUT_BOX_STYLE}
                    className={INPUT_BOX_CLASS}
                    {...invalidInputProps('employees', errors.employees, 'employees-hint')}
                  />
                  <p id="employees-hint" className="text-sm" style={{ color: '#4B5563' }}>
                    Enter the exact number of full-time employees in your company
                  </p>
                  <FieldError name="employees" message={errors.employees} />
                </div>

                <div className="space-y-3" {...fieldAnchorProps('annual_revenue_sgd')}>
                  <Label htmlFor="annual_revenue_sgd" className="font-medium" style={{ color: '#4B5563' }}>
                    Annual Revenue (SGD) <span style={{ color: '#4B5563' }}>*</span>
                  </Label>
                  <div className="checker-currency-field relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium leading-none" style={{ color: '#4B5563' }} aria-hidden>S$</span>
                    <Input
                      id="annual_revenue_sgd"
                      name="annual_revenue_sgd"
                      type="text"
                      inputMode="numeric"
                      value={formatNumberWithCommas(formData.annual_revenue_sgd)}
                      onChange={(e) => {
                        const sanitized = sanitizeNumberInput(e.target.value);
                        updateFormData({ annual_revenue_sgd: sanitized });
                      }}
                      placeholder="e.g., 250,000"
                      className={`${INPUT_BOX_CLASS} checker-currency-input !pl-[42px]`}
                      style={{ ...INPUT_BOX_STYLE, paddingLeft: '2.65rem' }}
                      {...invalidInputProps('annual_revenue_sgd', errors.annual_revenue_sgd, 'annual_revenue_sgd-hint')}
                    />
                  </div>
                  <p id="annual_revenue_sgd-hint" className="text-sm" style={{ color: '#4B5563' }}>
                    Enter your exact annual revenue in Singapore Dollars (e.g., 250000 for $250k)
                  </p>
                  <FieldError name="annual_revenue_sgd" message={errors.annual_revenue_sgd} />
                </div>
              </div>

              {/* Local Ownership and Business Stage Row */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3" {...fieldAnchorProps('local_ownership_pct')}>
                  <Label htmlFor="local_ownership_pct" className="font-medium" style={{ color: '#4B5563' }}>
                    Local Ownership Percentage <span style={{ color: '#4B5563' }}>*</span>
                  </Label>
                  <div className="flex items-center space-x-4">
                    <Input
                      id="local_ownership_pct"
                      name="local_ownership_pct"
                      type="text"
                      inputMode="numeric"
                      min="0"
                      max="100"
                      placeholder="e.g., 75"
                      value={formatNumberWithCommas(formData.local_ownership_pct)}
                      onChange={(e) => {
                        const sanitized = sanitizeNumberInput(e.target.value);
                        let parsed = parseInt(sanitized, 10);
                        if (Number.isNaN(parsed)) parsed = "";
                        if (parsed !== "") {
                          parsed = Math.min(Math.max(parsed, 0), 100);
                        }
                        updateFormData({ local_ownership_pct: parsed === "" ? "" : parsed.toString() });
                      }}
                      required
                      style={INPUT_BOX_STYLE}
                      className={INPUT_BOX_CLASS}
                      {...invalidInputProps('local_ownership_pct', errors.local_ownership_pct, 'local_ownership_pct-hint')}
                    />
                    <span className="font-medium text-lg" style={{ color: '#4B5563' }} aria-hidden>%</span>
                  </div>
                  <div id="local_ownership_pct-hint" className="text-sm bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3" style={{ color: '#4B5563' }}>
                    <p>Most government grants require at least 30% local ownership to ensure the benefits stay in Singapore.</p>
                  </div>
                  <FieldError name="local_ownership_pct" message={errors.local_ownership_pct} />
                </div>

                <div className="space-y-3" {...fieldAnchorProps('business_stage')}>
                  <Label id="business_stage-label" htmlFor="business_stage" className="font-medium" style={{ color: '#4B5563' }}>
                    Business Stage <span style={{ color: '#4B5563' }}>*</span>
                  </Label>
                  <Select value={formData.business_stage || ''} onValueChange={(value) => updateFormData({ business_stage: value })}>
                    <SelectTrigger
                      id="business_stage"
                      aria-labelledby="business_stage-label"
                      className={INPUT_BOX_CLASS}
                      style={INPUT_BOX_STYLE}
                      {...invalidInputProps('business_stage', errors.business_stage)}
                    >
                      <SelectValue placeholder="Select business stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {businessStageOptions.map(option => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError name="business_stage" message={errors.business_stage} />
                </div>
              </div>

              {/* Business Stage Guide */}
              <div className="checker-info-box">
                <div className="text-sm font-semibold mb-2">
                  Business Stage Guide:
                </div>
                <div className="space-y-2 text-xs">
                  <div>
                    <span className="font-semibold">Startup:</span> Early-stage company, typically
                    0-3 years, seeking product-market fit
                  </div>
                  <div>
                    <span className="font-semibold">Growth:</span> Established product/service,
                    expanding operations and market reach
                  </div>
                  <div>
                    <span className="font-semibold">Mature:</span> Well-established business with
                    stable operations and market position
                  </div>
                  <div>
                    <span className="font-semibold">Established:</span> Long-running company with a
                    proven track record and stable revenue
                  </div>
                </div>
              </div>

              {/* Financial Viability Section - Full Width */}
              <ChoiceGroup
                name="financial_viability"
                legend="Financial Viability"
                required
                hint="Is your company financially stable and able to co-fund the project?"
                error={errors.financial_viability}
                className="space-y-4"
              >
                <YesNoChoice
                  name="financial_viability"
                  value={formData.financial_viability}
                  onChange={(next) => updateFormData({ financial_viability: next })}
                  yesLabel="Yes, financially viable"
                  yesDescription="We have sufficient financial resources"
                  noLabel="No, not financially viable"
                  noDescription="We may need additional funding support"
                />
              </ChoiceGroup>

              {/* Cash runway / funding comfort */}
              <div className="space-y-4" {...fieldAnchorProps('cash_runway_band')}>
                <Label id="cash_runway_band-label" htmlFor="cash_runway_band" className="font-medium" style={{ color: '#4B5563' }}>
                  Operating Cash Runway <span style={{ color: '#4B5563' }}>*</span>
                </Label>
                <p id="cash_runway_band-hint" className="text-sm" style={{ color: '#4B5563' }}>
                  How much operating cash runway does the business currently have?
                </p>
                <Select
                  value={formData.cash_runway_band || ''}
                  onValueChange={(value) => updateFormData({ cash_runway_band: value })}
                >
                  <SelectTrigger
                    id="cash_runway_band"
                    aria-labelledby="cash_runway_band-label"
                    className={INPUT_BOX_CLASS}
                    style={INPUT_BOX_STYLE}
                    {...invalidInputProps('cash_runway_band', errors.cash_runway_band, 'cash_runway_band-hint')}
                  >
                    <SelectValue placeholder="Select an approximate runway" />
                  </SelectTrigger>
                  <SelectContent>
                    {cashRunwayOptions.map((option) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError name="cash_runway_band" message={errors.cash_runway_band} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
